import { useEffect, useState } from "react";
import { Box, Skeleton, useMediaQuery } from "@mui/material";
import CustomText, { textStyles } from "../../components/CustomText";
import { useTheme } from "@mui/material/styles";
import abouthero1 from "../../assets/abouthero1.png";
import aboutheroen from "../../assets/aboutheroen.png";
import aboutcard1 from "../../assets/aboutcard1.svg";
import aboutcard2 from "../../assets/aboutcard2.svg";
import aboutcard3 from "../../assets/aboutcard3.svg";
import { useTranslation } from "react-i18next";
import { BASE_URL } from "../../components/BASE_URL";

const HeroSection = () => {
  const { i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [loading, setLoading] = useState(true);
  const [heroTitle, setHeroTitle] = useState("");
  const [heroDesc, setHeroDesc] = useState("");
  const [cardsData, setCardsData] = useState([]);

  const heroImageSrc = i18n.language === "es" ? abouthero1 : aboutheroen;

useEffect(() => {
  setLoading(true);
  fetch(
    `${BASE_URL}content/sections?page=1&limit=10&sort=order&include=items&search=About_%20Precision1`
  )
    .then((res) => res.json())
    .then((json) => {
      console.log("About_Precision1 API response:", json);
      const section = json?.data?.[0];

      if (section?.items?.length) {
        const selectedItem =
          i18n.language === "es"
            ? section.items.find((item) => item.idx === 1)
            : section.items.find((item) => item.idx === 0);

        if (selectedItem) {
          setHeroTitle(selectedItem.title);
          setHeroDesc(selectedItem.description);

          const listItems = Array.isArray(selectedItem?.list)
            ? selectedItem.list
            : selectedItem?.list
              ? JSON.parse(selectedItem.list)
              : [];
          setCardsData([
            {
              icon: aboutcard1,
              title: listItems[0]?.name,
              description: listItems[0]?.value,
              color: "#006EC299",
            },
            {
              icon: aboutcard2,
              title: listItems[1]?.name,
              description: listItems[1]?.value,
              color: "#3EC1F399",
            },
            {
              icon: aboutcard3,
              title: listItems[2]?.name,
              description: listItems[2]?.value,
              color: "#00314999",
            },
          ]);
        }
      }
    })
    .catch(() => {
      setHeroTitle("Error loading data");
      setHeroDesc("Please try again later");
    })
    .finally(() => setLoading(false));
}, [i18n.language]);

const CardTitle = ({ title, color }) => (
  <CustomText
    style={{
      ...textStyles.h5,
      position: "relative",
      display: "inline-block",
      fontSize: "18px",
    }}
  >
    <Box
      component="span"
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: 10,
        height: 10,
        borderRadius: "50%",
        display: isMobile ? "none" : "block",
        backgroundColor: color,
        pointerEvents: "none",
      }}
    />
    <Box
      component="span"
      sx={{
        position: "relative",
        display: "inline-block",
        "&::after": {
          content: '""',
          position: "absolute",
          left: "50%",                 // start in the middle
          transform: "translateX(-50%)", // shift back half width
          bottom: 0,
          width: "50%",                // underline width
          borderBottom: `2px solid ${color}`,
          borderRadius: 1,
          pointerEvents: "none",
          lineHeight:"20px"
        },
      }}
    >
      {title}
    </Box>
  </CustomText>
);


  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: "center",
          justifyContent: "center",
          gap: isMobile ? 0 : 10,
          px: 2,
          py: 0,
          maxWidth: 1200,
          margin: "0 auto",
          marginTop: isMobile ? "20px" : "0px",
        }}
      >
        {/* Image */}
        <Box
          sx={{
            flexShrink: 0,
            maxWidth: isMobile ? "100%" : "50%",
            textAlign: "center",
          }}
        >
          <img
            src={heroImageSrc}
            alt="BiometricPRO"
            style={{
              width: "100%",
              height: "auto",
              borderRadius: 8,
              objectFit: "contain",
              display: "block",
              margin: "0 auto",
            }}
          />
        </Box>

        {/* Title + Description */}
        <Box
          sx={{
            flexGrow: 1,
            flexBasis: 0,
            py: isMobile ? 6 : 14,
            textAlign: isMobile ? "center" : "left",
          }}
        >
          {loading ? (
            <Skeleton height={50} width="80%" />
          ) : (
            <CustomText
              style={{
                ...textStyles.h1,
                color: "#072533",
                marginBottom: isMobile ? "0rem" : "1rem",
                marginTop: 0,
                fontSize: isMobile ? "40px" : "50px",
                textAlign: "inherit",
              }}
            >
              {heroTitle}
            </CustomText>
          )}

          {loading ? (
            <>
              <Skeleton height={28} width="90%" />
              <Skeleton height={28} width="70%" />
            </>
          ) : (
            <CustomText
              style={{
                ...textStyles.body2,
                color: "#5E5C5C",
                marginTop: "50px",
                fontSize: "24px",
                textAlign: "inherit",
                maxWidth: "100%",
              }}
            >
              {heroDesc}
            </CustomText>
          )}
        </Box>
      </Box>

      {/* Cards Section */}
      <Box
        sx={{
          mt: isMobile ? 3 : 5,
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: 2,
          maxWidth: 1150,
          margin: "0 auto",
          px: 3,
          paddingBottom: isMobile ? "30px" : "60px",
        }}
      >
        {loading
          ? [1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rectangular" width={270} height={150} sx={{ borderRadius: 2 }} />
            ))
          : cardsData.map(({ icon, title, description, color }, idx) => (
              <Box
                key={idx}
                sx={{
                  borderRadius: 2,
                  px: 1.5,
                  py: 3,
                  backgroundColor: "#fff",
                  minWidth: 270,
                  flex: "1 1 270px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 1,
                  cursor: "default",
                  border: "1px solid #DFDFDF",
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 1 }}>
                  <Box component="img" src={icon} sx={{ width: "40px", height: "40px", objectFit: "contain" }} />
                  <CardTitle title={title} color={color} />
                </Box>
                <CustomText
                  style={{
                    ...textStyles.body4,
                    textAlign: "left",
                    marginTop: "8px",
                    paddingLeft: "10px",
                    paddingRight: "10px",
                  }}
                >
                  {description}
                </CustomText>
              </Box>
            ))}
      </Box>
    </>
  );
};

export default HeroSection;







